import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { EventCategory } from '../event-category.js';

export class EventCategoryPrompt extends DataModel {
  static readonly TYPE_ID: string = '8f22dc8dd2b54f9f96338ccc9cc5c1d0';

  private constructor(
    public readonly category: EventCategory,
    public readonly useEmojis: boolean,
    public readonly vocabularyPrompt: string,
    public readonly lengthPrompt: string,
    public readonly examplesPrompt: string
  ) {
    super();
  }

  get $typeId(): string {
    return EventCategoryPrompt.TYPE_ID;
  }

  static create(args: {
    category: EventCategory,
    useEmojis: boolean,
    vocabularyPrompt: string,
    lengthPrompt?: string,
    examplesPrompt?: string
  }): EventCategoryPrompt {
    return new EventCategoryPrompt(
        args.category,
        args.useEmojis,
        args.vocabularyPrompt,
        args.lengthPrompt ?? '',
        args.examplesPrompt ?? ''
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventCategoryPrompt {
    return EventCategoryPrompt.create({
      category: toEnumFromString(MapUtils.getString(map, 'category'), EventCategory),
      useEmojis: MapUtils.getBoolean(map, 'useEmojis'),
      vocabularyPrompt: MapUtils.getString(map, 'vocabularyPrompt'),
      lengthPrompt: MapUtils.getString(map, 'lengthPrompt'),
      examplesPrompt: MapUtils.getString(map, 'examplesPrompt'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'category': fromEnumAsString<EventCategory>(this.category),
      'useEmojis': this.useEmojis,
      'vocabularyPrompt': this.vocabularyPrompt,
      'lengthPrompt': this.lengthPrompt,
      'examplesPrompt': this.examplesPrompt,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    category = this.category,
    useEmojis = this.useEmojis,
    vocabularyPrompt = this.vocabularyPrompt,
    lengthPrompt = this.lengthPrompt,
    examplesPrompt = this.examplesPrompt,
  }: {
    category?: EventCategory,
    useEmojis?: boolean,
    vocabularyPrompt?: string,
    lengthPrompt?: string,
    examplesPrompt?: string,
  } = {}): this {
    return EventCategoryPrompt.create({
      category,
      useEmojis,
      vocabularyPrompt,
      lengthPrompt,
      examplesPrompt,
    }) as this;
  }

  isSame(other: EventCategoryPrompt): boolean {
    if(other === this) {
      return true;
    }

    return other.category === this.category &&
      other.useEmojis === this.useEmojis &&
      other.vocabularyPrompt === this.vocabularyPrompt &&
      other.lengthPrompt === this.lengthPrompt &&
      other.examplesPrompt === this.examplesPrompt;
  }
}

export class EventCategoryPromptBuilder extends DataModelBuilder<EventCategoryPrompt> {
  build(context: SerializationContext, map: SerializedData): EventCategoryPrompt {
    return EventCategoryPrompt.fromMap(context, map);
  }
}
